// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState, useEffect, Suspense } from 'react';
import axios from 'axios';
import UpgradeModal from './components/UpgradeModal';
import WelcomeTour from './components/WelcomeTour';
import LegalPageModal from './components/LegalPageModal';
import { validateBarcode } from './utils/barcodeValidator';
import SessionManager from './utils/sessionManager';
import AuthScreen from './components/views/AuthScreen';
import DashboardView from './components/views/DashboardView';
import UserViewRouter from './components/views/UserViewRouter';
import BetaWelcomeModal from './components/BetaWelcomeModal';
import FounderWelcomeModal from './components/FounderWelcomeModal';
import ProWelcomeModal from './components/ProWelcomeModal';
import FreeWelcomeModal from './components/FreeWelcomeModal';
import { useNavigate } from 'react-router-dom';
import AnalyticsTracker from './utils/analyticsTracker';
import { trackRegisterCompleted, trackLoginCompleted, trackPurchaseCompleted } from './utils/tracking';
import { NotificationBell, NotificationPopup, PushNotificationOptIn } from './components/NotificationComponents';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with all requests
axios.defaults.withCredentials = true;

// Auto-refresh interceptor for global axios
let isGlobalRefreshing = false;
let globalFailedQueue = [];

const processGlobalQueue = (error) => {
  globalFailedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  globalFailedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // iter121 defensive (2026-02-12): 403 on an authed endpoint means a stale
    // JWT is pointing at a user_id that no longer matches the current resource
    // owner. Cookie-only clear + redirect to landing forces a fresh login.
    if (
      error.response?.status === 403 &&
      originalRequest?.url &&
      !originalRequest.url.includes('/auth/login') &&
      !originalRequest.url.includes('/auth/logout') &&
      !originalRequest.url.includes('/admin/login')
    ) {
      try {
        // Wipe local + session storage (PWA caches, prefs, etc.)
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        sessionStorage.clear();
        // Best-effort: ask server to wipe its httpOnly cookies too.
        try { await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }); } catch {}
      } catch {}
      // Redirect to landing where the user can re-login.
      if (typeof window !== 'undefined' && !window.location.pathname.match(/^\/(login|app)?$/)) {
        window.location.href = '/?session_expired=1';
      } else if (typeof window !== 'undefined') {
        window.location.reload();
      }
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/admin/login')
    ) {
      if (isGlobalRefreshing) {
        return new Promise((resolve, reject) => {
          globalFailedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }
      originalRequest._retry = true;
      isGlobalRefreshing = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        processGlobalQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processGlobalQueue(refreshError);
        return Promise.reject(error);
      } finally {
        isGlobalRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

// Push notification subscription helper
async function subscribeToPushNotifications(userId) {
  try {
    if (!('Notification' in window)) return;
    
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;
    
    const registration = await navigator.serviceWorker?.ready;
    if (!registration) return;
    
    // Subscribe to push using real VAPID public key
    const vapidPublicKey = 'BHgMUoQ5im1zUsaEhsPAnzgCLYGUeLMw2cc_0MoMRfTZnQ-gB47cLvKkQmBxCdw0azHnhR5M5aR2YbQjwEal3eU';
    const urlBase64ToUint8Array = (base64String) => {
      const padding = '='.repeat((4 - base64String.length % 4) % 4);
      const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);
      for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
      return outputArray;
    };
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    }).catch(() => null);
    
    if (subscription) {
      const subJson = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        user_id: userId,
        endpoint: subJson.endpoint,
        keys: subJson.keys || {}
      }).catch(() => {}); // Silent fail - push is optional
    }
  } catch (e) {
    // Push notifications are optional - don't break login flow
  }
}

// UK Retailers list
const UK_RETAILERS = [
  'Tesco',
  'Sainsburys',
  'Asda',
  'Morrisons',
  'Aldi',
  'Lidl',
  'Waitrose',
  'Co-op',
  'Iceland',
  'Marks & Spencer'
];

// Contributor Level Badges
const LEVEL_INFO = {
  1: { name: 'Novice', color: 'bg-gray-500', icon: '🌱', multiplier: '1.0x' },
  2: { name: 'Contributor', color: 'bg-blue-500', icon: '⭐', multiplier: '1.2x' },
  3: { name: 'Expert', color: 'bg-purple-500', icon: '💎', multiplier: '1.5x' },
  4: { name: 'Guru', color: 'bg-yellow-500', icon: '👑', multiplier: '2.0x' }
};

// Views that belong to each parent tab - for back button navigation
const SCAN_TAB_CHILDREN = ['submit', 'receipt'];
const COMPARE_TAB_CHILDREN = ['compare', 'compare-single', 'bulk'];
const MORE_TAB_CHILDREN = [
  'fuel-prices', 'fuel-savings', 'wallet', 'alerts', 
  'family', 'referral', 'recipes', 'founders', 'settings', 'help',
  'privacy', 'terms', 'cookies', 'contact', 'history', 
  'kitchen', 'business-portal', 'upgrade-view', 'newsletter', 'my-blog',
  'pricing'
];

// Legacy constant for backwards compatibility
const MORE_TAB_SUBPAGES = MORE_TAB_CHILDREN;

function UserApp({ isBetaSignup = false, forceSignupMode = false, redirectToAppAfterSignup = false }) {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Default to signup if arriving from beta link OR the new /register route
  const [isSignup, setIsSignup] = useState(isBetaSignup || forceSignupMode);
  const [activeView, setActiveView] = useState('dashboard');
  const [activeTab, setActiveTab] = useState('home'); // Bottom nav: home, scan, compare, more
  const [navigationHistory, setNavigationHistory] = useState(['dashboard']);
  const [moreReturnSection, setMoreReturnSection] = useState(null); // Track which More section to return to
  const [legalPageModal, setLegalPageModal] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Shopping List state
  const [shoppingListItems, setShoppingListItems] = useState([]);
  
  // TIER SYSTEM STATE
  const [userTier, setUserTier] = useState('free'); // 'free', 'pro', 'family'
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');

  // Auth states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [name, setName] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [authError, setAuthError] = useState('');
  const [userId, setUserId] = useState('');
  const [referralCode, setReferralCode] = useState(''); // Referral code input field

  // Phone verification states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);

  // User data
  const [userData, setUserData] = useState(null);
  const [userSubmissions, setUserSubmissions] = useState([]);
  const [receiptHistory, setReceiptHistory] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [hasSeenBetaModal, setHasSeenBetaModal] = useState(() => {
    return localStorage.getItem('pricepal_beta_modal_seen') === 'true';
  });
  const [showFounderModal, setShowFounderModal] = useState(false);
  const [hasSeenFounderModal, setHasSeenFounderModal] = useState(() => {
    return localStorage.getItem('pricepal_founder_modal_seen') === 'true';
  });
  const [founderModalTier, setFounderModalTier] = useState(null);
  const [showProModal, setShowProModal] = useState(false);
  const [hasSeenProModal, setHasSeenProModal] = useState(false);
  const [isProFamily, setIsProFamily] = useState(false);
  const [showFreeModal, setShowFreeModal] = useState(false);
  const [hasSeenFreeModal, setHasSeenFreeModal] = useState(false);

  // Welcome Tour state
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);

  const handleCloseTour = () => {
    setShowWelcomeTour(false);
    if (userId) {
      localStorage.setItem(`pricepal_tour_seen_${userId}`, 'true');
    }
  };

  const handleOpenTour = () => {
    setShowWelcomeTour(true);
  };

  // Smart navigation handler
  const handleNavigation = (view, data) => {
    if (view === 'legal') {
      // Open legal page modal
      setLegalPageModal(data?.page);
    } else if (view === 'upgrade') {
      // Navigate to full-screen UpgradeView (has Stripe checkout integration)
      setNavigationHistory(prev => [...prev, 'upgrade-view']);
      setActiveView('upgrade-view');
    } else if (view === 'tour') {
      // Re-open the Welcome Tour
      setShowWelcomeTour(true);
    } else if (view === 'back') {
      // Go back in history
      if (navigationHistory.length > 1) {
        const newHistory = [...navigationHistory];
        newHistory.pop(); // Remove current view
        const previousView = newHistory[newHistory.length - 1];
        setNavigationHistory(newHistory);
        setActiveView(previousView);
      } else {
        setActiveView('dashboard');
      }
    } else {
      // Normal navigation - add to history
      setNavigationHistory(prev => [...prev, view]);
      setActiveView(view);
    }
  };

  // Back button handler - returns to parent tab based on current view
  const handleBack = () => {
    window.scrollTo(0, 0);
    // If current view is a Scan tab child, go back to Scan parent
    if (SCAN_TAB_CHILDREN.includes(activeView)) {
      setActiveView('scan-parent');
      setActiveTab('scan');
      return;
    }
    
    // If current view is a Compare tab child, go back to Compare parent
    if (COMPARE_TAB_CHILDREN.includes(activeView)) {
      setActiveView('compare-parent');
      setActiveTab('compare');
      return;
    }
    
    // If current view is a More tab child, go back to the parent tab the user came from
    if (MORE_TAB_CHILDREN.includes(activeView)) {
      // Return to the tab the user was on when they entered this view
      if (activeTab === 'home') {
        setActiveView('dashboard');
        setActiveTab('home');
        return;
      }
      if (activeTab === 'compare') {
        setActiveView('compare-parent');
        setActiveTab('compare');
        return;
      }
      if (activeTab === 'scan') {
        setActiveView('scan-parent');
        setActiveTab('scan');
        return;
      }
      
      // Came from More tab — return to correct section
      const featuresViews = ['fuel-prices', 'fuel-savings', 'kitchen', 'recipes'];
      const rewardsViews = ['upgrade-view', 'pricing', 'founders', 'wallet', 'referral', 'newsletter', 'my-blog'];
      const accountViews = ['settings', 'family'];
      const supportViews = ['help', 'contact'];
      const activityViews = ['history', 'alerts'];
      const legalViews = ['privacy', 'terms', 'cookies'];
      
      if (featuresViews.includes(activeView)) setMoreReturnSection('features');
      else if (rewardsViews.includes(activeView)) setMoreReturnSection('rewards');
      else if (accountViews.includes(activeView)) setMoreReturnSection('account');
      else if (supportViews.includes(activeView)) setMoreReturnSection('support');
      else if (activityViews.includes(activeView)) setMoreReturnSection('activity');
      else if (legalViews.includes(activeView)) setMoreReturnSection('legal');
      else setMoreReturnSection(null);
      
      setActiveView('more');
      setActiveTab('more');
      return;
    }
    
    // For parent tabs (scan-parent, compare-parent, more), go to home
    if (['scan-parent', 'compare-parent', 'more'].includes(activeView)) {
      setActiveView('dashboard');
      setActiveTab('home');
      return;
    }
    
    // Default: go to dashboard
    setActiveView('dashboard');
    setActiveTab('home');
  };
  
  // Handle bottom tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    window.scrollTo(0, 0);
    switch (tab) {
      case 'home':
        setActiveView('dashboard');
        break;
      case 'scan':
        setActiveView('scan-parent');
        break;
      case 'compare':
        setActiveView('compare-parent');
        break;
      case 'more':
        setActiveView('more');
        break;
      default:
        setActiveView('dashboard');
    }
  };

  // Scan form states
  const [barcode, setBarcode] = useState('');
  const [productName, setProductName] = useState('');
  const [price, setPrice] = useState('');
  // SIMPLE RETAILER PERSISTENCE - Use localStorage
  const [retailer, setRetailer] = useState(
    localStorage.getItem('lastRetailer') || 'Tesco'
  );
  const [scanSuccess, setScanSuccess] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [scanError, setScanError] = useState('');
  const [productImage, setProductImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [lastSubmittedProduct, setLastSubmittedProduct] = useState(null); // Store last submission for Set Alert button
  const [quickSubmitMode, setQuickSubmitMode] = useState(false); // Streamlined mode when product exists in DB
  const [foundProduct, setFoundProduct] = useState(null); // Product data from DB lookup
  const [autoFilledName, setAutoFilledName] = useState(''); // Track original auto-filled name for correction detection
  
  // ENHANCED: Price Type & Discount Context
  const [priceType, setPriceType] = useState('standard');
  const [originalPrice, setOriginalPrice] = useState('');
  const [offerDetails, setOfferDetails] = useState('');

  // Price comparison states
  const [searchBarcode, setSearchBarcode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('barcode'); // 'barcode' or 'name'
  const [searchResults, setSearchResults] = useState([]);
  const [priceData, setPriceData] = useState(null);
  const [priceError, setPriceError] = useState('');

  // Load user session on mount - ENHANCED with SessionManager
  useEffect(() => {
    
    // Use SessionManager for centralized session handling
    const session = SessionManager.init();
    
    if (session && session.userId && session.userEmail) {
      
      setUserId(session.userId);
      setEmail(session.userEmail);
      setIsAuthenticated(true);
      
      // IMPORTANT: Set userData IMMEDIATELY from session storage to trigger modal
      if (session.userData) {
        setUserData(session.userData);
        
        // Explicitly check for beta status here on mount
        const initialTier = (session.userData.subscription_tier || 'FREE').toUpperCase();
        const isBeta = 
          session.userData.is_beta_tester || 
          session.userData.is_alpha_tester ||
          session.userData.lifetime_access ||
          initialTier === 'ALPHA_TESTER' ||
          initialTier === 'VIP_FOUNDER' ||
          initialTier === 'EARLY_FOUNDER';
          
        if (isBeta && !hasSeenBetaModal) {
           setShowBetaModal(true);
           setHasSeenBetaModal(true);
           localStorage.setItem('pricepal_beta_modal_seen', 'true');
        }

        // Check for VIP/Early Founder welcome
        const isVIPFounder = initialTier === 'VIP_FOUNDER';
        const isEarlyFounder = initialTier === 'EARLY_FOUNDER';
        if ((isVIPFounder || isEarlyFounder) && !hasSeenFounderModal) {
          setFounderModalTier(isVIPFounder ? 'VIP_FOUNDER' : 'EARLY_FOUNDER');
          setShowFounderModal(true);
          setHasSeenFounderModal(true);
          localStorage.setItem('pricepal_founder_modal_seen', 'true');
        }

        // Check for Individual / Family welcome
        const isIndividualPro = initialTier === 'INDIVIDUAL';
        const isFamilyPro = initialTier === 'FAMILY';
        if ((isIndividualPro || isFamilyPro) && !isBeta && !hasSeenProModal) {
          setIsProFamily(isFamilyPro);
          setShowProModal(true);
          setHasSeenProModal(true);
        }

        // Check for Free/Trial user welcome
        const isFreeOrTrial = initialTier === 'FREE' || initialTier === 'PRO_TRIAL';
        if (isFreeOrTrial && !isBeta && !hasSeenFreeModal) {
          setShowFreeModal(true);
          setHasSeenFreeModal(true);
        }
      }
      
      fetchUserData(session.userId, session.userData);
      fetchUserSubmissions(session.userId);
    } else {
      SessionManager.clearSession();
    }
  }, []); // Empty dependency array - runs once on mount

  // Track page/view changes for analytics
  useEffect(() => {
    if (isAuthenticated && activeView) {
      AnalyticsTracker.trackPageView();
      const featureViews = ['fuel-prices', 'fuel-savings', 'kitchen', 'recipes', 'compare', 'receipt', 'submit', 'wallet', 'newsletter'];
      if (featureViews.includes(activeView)) {
        AnalyticsTracker.trackFeature(activeView);
      }
    }
  }, [activeView, isAuthenticated]);

  // Check for Stripe payment success on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId) {
      // Poll Stripe for payment confirmation
      const pollPaymentStatus = async (attempts = 0) => {
        if (attempts >= 5) return;
        try {
          const response = await axios.get(`${API}/stripe-payments/checkout/status/${sessionId}`, { withCredentials: true });
          if (response.data.payment_status === 'paid') {
            alert(`🎉 Payment successful! You've been upgraded to ${response.data.tier_name || 'Pro'}. Enjoy your new features!`);
            // iter158 — Server-confirmed purchase event. Only fires when Stripe
            // reports payment_status === 'paid'. This is the true ROAS
            // conversion event Google Ads should optimise on.
            try {
              const amount = Number(response.data.amount_total || 0) / 100; // Stripe returns pence
              trackPurchaseCompleted(
                userId,
                response.data.tier_name || response.data.tier || 'subscription',
                amount,
                (response.data.currency || 'GBP').toUpperCase(),
                sessionId,
              );
            } catch { /* tracking must never break the payment flow */ }
            // Clean URL and refresh user data
            window.history.replaceState({}, '', '/app');
            if (userId) fetchUserData(userId);
          } else if (response.data.status === 'expired') {
            alert('Payment session expired. Please try again.');
            window.history.replaceState({}, '', '/app');
          } else {
            // Still processing — poll again
            setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
          }
        } catch (error) {
          console.error('Payment status check error:', error);
        }
      };
      pollPaymentStatus();
    } else if (paymentStatus === 'cancelled') {
      window.history.replaceState({}, '', '/app');
    }

    // Handle ?view= deep links (from landing page, emails, etc.)
    const viewParam = urlParams.get('view');
    const actionParam = urlParams.get('action');
    
    // Valid deep-link views that can be navigated to from URL parameters
    const VALID_DEEP_LINK_VIEWS = [
      'upgrade-view', 'pricing', 'compare-parent', 'fuel-prices',
      'kitchen', 'recipes', 'settings', 'founders', 'history', 'alerts',
      'scan-parent', 'newsletter', 'referral', 'wallet',
      'help', 'contact', 'family', 'fuel-savings', 'my-blog',
      'privacy', 'terms', 'cookies'
    ];
    
    if (viewParam && isAuthenticated) {
      // Phase 3: `?view=fuel` alias resolves to `fuel-prices` (audit doc / iter113 advisory)
      const resolvedView = viewParam === 'fuel' ? 'fuel-prices' : viewParam;
      if (VALID_DEEP_LINK_VIEWS.includes(resolvedView)) {
        setActiveView(resolvedView);
        // Set the correct tab for context
        if (['compare-parent'].includes(resolvedView)) setActiveTab('compare');
        else if (['scan-parent'].includes(resolvedView)) setActiveTab('scan');
        else if (['pricing', 'founders', 'kitchen', 'recipes', 'fuel-prices', 'fuel-savings', 'settings', 'help', 'contact', 'newsletter', 'referral', 'wallet', 'my-blog', 'history', 'alerts', 'family', 'privacy', 'terms', 'cookies'].includes(resolvedView)) setActiveTab('more');
        window.history.replaceState({}, '', '/app');
      }
    } else if (viewParam && !isAuthenticated) {
      // Store intended view for after login (preserve `fuel` alias too)
      localStorage.setItem('pricepal_pending_view', viewParam === 'fuel' ? 'fuel-prices' : viewParam);
    }
    
    if (actionParam === 'manage-billing' && isAuthenticated) {
      setActiveView('settings');
      window.history.replaceState({}, '', '/app');
      // Auto-open Stripe billing portal
      setTimeout(async () => {
        try {
          const portalRes = await axios.post(`${API}/stripe-payments/portal/create`, {}, { withCredentials: true });
          if (portalRes.data?.url) {
            window.open(portalRes.data.url, '_blank');
          }
        } catch (e) {
          // Portal may fail for free users — Settings page handles it
        }
      }, 1500);
    } else if (actionParam === 'nhs-checkout' && isAuthenticated) {
      window.history.replaceState({}, '', '/app');
      // Auto-create Community Stripe checkout (no trial — they already had it)
      setTimeout(async () => {
        try {
          const res = await axios.post(`${API}/stripe-payments/checkout/create`, {
            tier_id: 'community',
            billing_cycle: 'monthly',
            origin_url: window.location.origin
          }, { withCredentials: true });
          if (res.data?.checkout_url) {
            window.location.href = res.data.checkout_url;
          }
        } catch (e) {
          setActiveView('upgrade-view');
        }
      }, 500);
    }
  }, [userId, isAuthenticated]); // eslint-disable-line

  // Validate session on view changes - CRITICAL FIX for tab navigation
  useEffect(() => {
    if (activeView !== 'dashboard' && activeView !== 'compare') {
      
      if (!SessionManager.isValid()) {
        
        // Clear state and redirect to login
        setIsAuthenticated(false);
        setUserId('');
        setEmail('');
        setActiveView('dashboard');
        
        alert('Your session has expired. Please log in again.');
      } else {
        // Update activity timestamp
        SessionManager.updateActivity();
      }
    }
  }, [activeView]);

  // Scroll to top on every view change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeView]);

  const fetchUserSubmissions = async (uid) => {
    try {
      const response = await axios.get(`${API}/user/submissions`, {
        params: { user_id: uid, limit: 50 },
        withCredentials: true
      });
      
      if (response?.data?.submissions) {
        setUserSubmissions(response.data.submissions);
      }
      
      // Also fetch receipt history
      try {
        const receiptRes = await axios.get(`${API}/price-hub/my-receipts`, {
          params: { user_id: uid, limit: 50 },
          withCredentials: true
        });
        if (receiptRes?.data) {
          setReceiptHistory(receiptRes.data);
        }
      } catch (receiptErr) {
      }
    } catch (error) {
      console.error('Error fetching user submissions:', error);
      setUserSubmissions([]);
    }
  };
  const fetchUserData = async (uid, initialUserData = null) => {
    // Defensive: Validate uid parameter
    if (!uid || typeof uid !== 'string') {
      console.error('Invalid user ID provided to fetchUserData');
      return;
    }

    try {
      // Fetch user progress and subscription data (NO admin endpoints)
      const [progressRes, subscriptionRes] = await Promise.all([
        axios.get(`${API}/users/${uid}/progress`).catch(err => {
          return null;
        }),
        axios.get(`${API}/users/${uid}/subscription`).catch(err => {
          return null;
        })
      ]);
      
      // Defensive: Validate response data before setting state
      // IMPORTANT: MERGE with initialUserData to preserve beta tester fields!
      // Progress data only has stats (points, submissions) — subscription fields come from login/subscription endpoints
      const progressData = progressRes?.data || {};
      const subData = subscriptionRes?.data || {};
      
      // Build userData by layering: initial login data → subscription data → progress stats
      // Subscription fields take priority over progress (which doesn't have them)
      const mergedUserData = {
        ...(initialUserData || {}),
        ...progressData,
        // Always preserve subscription/access fields from login or subscription endpoint
        subscription_tier: subData.subscription_tier || subData.tier || initialUserData?.subscription_tier || 'FREE',
        is_alpha_tester: subData.is_alpha_tester ?? initialUserData?.is_alpha_tester ?? false,
        is_beta_tester: subData.is_beta_tester ?? initialUserData?.is_beta_tester ?? false,
        lifetime_access: subData.lifetime_access ?? initialUserData?.lifetime_access ?? false,
        trial_active: subData.trial_active ?? initialUserData?.trial_active ?? false,
      };
      
      setUserData(prev => ({
        ...(prev || {}),
        ...mergedUserData
      }));
      
      if (subscriptionRes?.data) {
        setSubscriptionInfo(subscriptionRes.data);
        // Set user tier from subscription data
        if (subscriptionRes.data.tier) {
          setUserTier(subscriptionRes.data.tier);
        }
      } else {
        // Default to free tier if no subscription data
        setUserTier('free');
      }
      
      // BETA MODAL TRIGGER
      // Check if user is a beta/alpha tester AND hasn't seen the modal in this session yet
      // Check initialUserData OR subscription response, normalized to uppercase
      const tier = (subscriptionRes?.data?.tier || initialUserData?.subscription_tier || 'FREE').toUpperCase();
      
      const isBetaUser = 
        initialUserData?.is_beta_tester || 
        initialUserData?.is_alpha_tester ||
        initialUserData?.lifetime_access ||
        tier === 'ALPHA_TESTER' ||
        tier === 'VIP_FOUNDER' ||
        tier === 'EARLY_FOUNDER';

      if (isBetaUser && !hasSeenBetaModal) {
        setShowBetaModal(true);
        setHasSeenBetaModal(true);
        localStorage.setItem('pricepal_beta_modal_seen', 'true');
      }

      // FOUNDER MODAL TRIGGER - for VIP and Early Founders
      const isVIPFounder = tier === 'VIP_FOUNDER';
      const isEarlyFounder = tier === 'EARLY_FOUNDER';
      if ((isVIPFounder || isEarlyFounder) && !hasSeenFounderModal) {
        setFounderModalTier(isVIPFounder ? 'VIP_FOUNDER' : 'EARLY_FOUNDER');
        setShowFounderModal(true);
        setHasSeenFounderModal(true);
        localStorage.setItem('pricepal_founder_modal_seen', 'true');
      }

      // PRO MODAL TRIGGER - for Individual and Family
      const isIndividualPro = tier === 'INDIVIDUAL';
      const isFamilyPro = tier === 'FAMILY';
      if ((isIndividualPro || isFamilyPro) && !isBetaUser && !hasSeenProModal) {
        setIsProFamily(isFamilyPro);
        setShowProModal(true);
        setHasSeenProModal(true);
      }

      // FREE USER MODAL TRIGGER - for free/trial users
      const isFreeOrTrial = tier === 'FREE' || tier === 'PRO_TRIAL';
      if (isFreeOrTrial && !isBetaUser && !hasSeenFreeModal) {
        setShowFreeModal(true);
        setHasSeenFreeModal(true);
      }
      
    } catch (error) {
      console.error('Error fetching user data:', error);
      // Keep initial login data on error, or use defaults
      if (initialUserData) {
        setUserData(initialUserData);
      } else {
        setUserData({
          points_balance: 0,
          total_submissions: 0,
          approved_submissions: 0,
          rejected_submissions: 0,
          contributor_level: 1,
          approval_rate: 0
        });
      }
    }
  };

  // ── Phone number helpers ────────────────────────────────────────────────────
  const validateUKPhone = (raw) => {
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) return { valid: false, error: '' }; // empty = optional, skip
    const stripped = digits.startsWith('44') ? digits.slice(2) : digits.startsWith('0') ? digits.slice(1) : digits;
    if (!/^[7-9]\d{9}$/.test(stripped)) {
      return { valid: false, error: 'Enter a valid UK mobile number (e.g. 07700 900123 or +44 7700 900123)' };
    }
    return { valid: true, error: '' };
  };

  const handleSendOTP = async () => {
    if (!phoneNumber.trim()) return;
    const check = validateUKPhone(phoneNumber);
    if (!check.valid) { setPhoneError(check.error); return; }
    setPhoneError('');
    setOtpError('');
    setOtpLoading(true);
    try {
      await axios.post(`${API}/auth/send-phone-otp`, { phone_number: phoneNumber });
      setOtpSent(true);
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Could not send verification code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) { setOtpError('Please enter the 6-digit code.'); return; }
    setOtpError('');
    setOtpLoading(true);
    try {
      await axios.post(`${API}/auth/verify-phone-otp`, {
        phone_number: phoneNumber,
        code: otpCode,
        whatsapp_opted_in: whatsappOptIn
      });
      setOtpVerified(true);
      setOtpError('');
    } catch (err) {
      setOtpError(err.response?.data?.detail || 'Incorrect code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Beta Choice Modal callback
  // Registration is now handled directly in handleAuth — no modal needed


  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    // Defensive: Validate email and password
    if (!email || !email.trim()) {
      setAuthError('Email is required');
      setLoading(false);
      return;
    }

    if (!password || password.length < 6) {
      setAuthError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      if (isSignup) {
        // Detect referral code from URL or input
        const urlParams = new URLSearchParams(window.location.search);
        const refCode = urlParams.get('ref');
        const effectiveReferralCode = refCode 
          || (referralCode.trim() ? referralCode.trim() : null)
          || (isBetaSignup && isSignup ? 'BETA001' : null);
        
        // Register directly — backend auto-assigns the best available tier
        const formData = new FormData();
        formData.append('email', email.trim());
        formData.append('password', password);
        formData.append('name', name.trim());
        formData.append('marketing_consent', marketingConsent);
        formData.append('tier_choice', 'trial'); // 2026-06-29: founder programme closed — always PRO_TRIAL
        if (effectiveReferralCode) {
          formData.append('referral_code', effectiveReferralCode);
        }

        // 📊 iter141c — attach first-touch attribution captured on landing page
        try {
          const attr = JSON.parse(window.localStorage.getItem('pp_attribution') || '{}');
          const anon = window.localStorage.getItem('pp_anon_id');
          ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','fbclid'].forEach((k) => {
            if (attr[k]) formData.append(k, attr[k]);
          });
          if (attr.referrer) formData.append('landing_referrer', attr.referrer);
          if (attr.landing_url) formData.append('landing_url', attr.landing_url);
          if (anon) formData.append('anon_id', anon);
        } catch { /* attribution is optional */ }
        
        const response = await axios.post(`${API}/auth/register`, formData, {
          withCredentials: true
        });
        
        // Validate response
        if (!response?.data?.user?.id) {
          throw new Error('Invalid server response - no user ID returned');
        }
        
        // Store user info in state and SessionManager
        setUserId(response.data.user.id);
        setEmail(email.trim());
        setIsAuthenticated(true);
        
        // Use SessionManager for persistent storage
        SessionManager.setSession(
          response.data.user.id,
          email.trim(),
          response.data.user,
          rememberMe
        );
        
        fetchUserData(response.data.user.id);
        fetchUserSubmissions(response.data.user.id);

        // Track signup as login for analytics
        AnalyticsTracker.trackLogin(response.data.user.id);

        // iter158 — Fire the server-confirmed `sign_up` conversion event.
        // Fires ONLY here (after backend returns a valid user id) so Google
        // Ads / GA4 never counts an existing user login as a registration.
        trackRegisterCompleted(response.data.user.id, 'email', {
          tier: response.data.user.subscription_tier || 'PRO_TRIAL',
          marketing_consent: marketingConsent,
        });

        // iter158 — When arriving via the new /register route, land the
        // freshly-registered user on /app so they see the dashboard.
        if (redirectToAppAfterSignup) {
          navigate('/app', { replace: true });
        }

        // Show welcome tour for new signups (check localStorage to show only once)
        const tourKey = `pricepal_tour_seen_${response.data.user.id}`;
        if (!localStorage.getItem(tourKey)) {
          setTimeout(() => setShowWelcomeTour(true), 1500);
        }
      } else {
        // Login with JWT authentication (cookies)
        const formData = new FormData();
        formData.append('email', email.trim());
        formData.append('password', password);
        
        let response;
        try {
          response = await axios.post(`${API}/auth/login`, formData, {
            withCredentials: true
          });
        } catch (loginErr) {
          // Retry once on 404 (transient deployment/routing issue)
          if (loginErr.response?.status === 404) {
            await new Promise(r => setTimeout(r, 1500));
            response = await axios.post(`${API}/auth/login`, formData, {
              withCredentials: true
            });
          } else {
            throw loginErr;
          }
        }
        
        // Validate response
        if (!response?.data?.user?.id) {
          throw new Error('Invalid server response - no user ID returned');
        }
        
        // Store user info in state and SessionManager
        setUserId(response.data.user.id);
        setEmail(email.trim());
        setIsAuthenticated(true);
        
        // IMPORTANT: Store complete login data first (includes beta tester fields!)
        setUserData(response.data.user);
        
        // Use SessionManager for persistent storage
        SessionManager.setSession(
          response.data.user.id,
          email.trim(),
          response.data.user,
          rememberMe
        );
        
        // Subscribe to push notifications (optional, silent fail)
        subscribeToPushNotifications(response.data.user.id);

        // Track login for analytics
        AnalyticsTracker.trackLogin(response.data.user.id);

        // iter158 — Server-confirmed login event (distinct from sign_up).
        // Fired only after a successful `/api/auth/login`.
        trackLoginCompleted(response.data.user.id);
        
        // Check for pending checkout after login
        const pendingCheckout = localStorage.getItem('pendingCheckout');
        if (pendingCheckout) {
          try {
            const checkout = JSON.parse(pendingCheckout);
            
            // Clear the pending checkout
            localStorage.removeItem('pendingCheckout');
            
            // Instead of redirecting to pricing, go directly to Stripe checkout
            try {
              // Map tier key to API tier_id
              const tierMapping = {
                'INDIVIDUAL': 'individual',
                'FAMILY': 'family',
                'VIP_FOUNDER': 'vip_founder',
                'EARLY_FOUNDER': 'early_founder',
                'COMMUNITY': 'community',
                'NHS_STAFF': 'community'  // legacy alias
              };
              const tierKey = tierMapping[checkout.tier] || checkout.tier.toLowerCase();
              
              // Call Stripe checkout API
              const checkoutResponse = await axios.post(
                `${API}/stripe-payments/checkout/create`,
                {
                  tier_id: tierKey,
                  billing_cycle: checkout.billingCycle || 'monthly',
                  origin_url: window.location.origin
                },
                { withCredentials: true }
              );
              
              if (checkoutResponse.data.success && checkoutResponse.data.checkout_url) {
                window.location.href = checkoutResponse.data.checkout_url;
                return;
              }
            } catch (checkoutError) {
              console.error('Checkout error after login:', checkoutError);
              // Fall through to normal app view
            }
          } catch (e) {
            console.error('Error processing pending checkout:', e);
            localStorage.removeItem('pendingCheckout');
          }
        }
        
        // Also check URL params for checkout intent (fallback when localStorage blocked)
        const urlParams = new URLSearchParams(window.location.search);
        const checkoutTier = urlParams.get('tier');
        const checkoutBilling = urlParams.get('billing') || 'monthly';
        if (checkoutTier) {
          
          try {
            const tierMapping = {
              'INDIVIDUAL': 'individual',
              'FAMILY': 'family',
              'VIP_FOUNDER': 'vip_founder',
              'EARLY_FOUNDER': 'early_founder',
              'COMMUNITY': 'community',
              'NHS_STAFF': 'community'  // legacy alias
            };
            const tierKey = tierMapping[checkoutTier] || checkoutTier.toLowerCase();
            
            const checkoutResponse = await axios.post(
              `${API}/stripe-payments/checkout/create`,
              {
                tier_id: tierKey,
                billing_cycle: checkoutBilling,
                origin_url: window.location.origin
              },
              { withCredentials: true }
            );
            
            if (checkoutResponse.data.success && checkoutResponse.data.checkout_url) {
              window.location.href = checkoutResponse.data.checkout_url;
              return;
            }
          } catch (checkoutError) {
            console.error('Checkout error from URL params:', checkoutError);
          }
        }
        
        // Fetch additional data and MERGE (don't overwrite)
        fetchUserData(response.data.user.id, response.data.user);
        fetchUserSubmissions(response.data.user.id);

        // Handle email deep links after login (e.g. ?view=upgrade-view or ?action=manage-billing)
        const viewParam = urlParams.get('view');
        const actionParam = urlParams.get('action');

        // Handle NHS redirect after login
        const nhsRedirect = sessionStorage.getItem('nhs_redirect');
        if (nhsRedirect) {
          sessionStorage.removeItem('nhs_redirect');
          navigate('/nhs');
          return;
        }

        // Check for pending view from deep link (stored before login)
        const pendingView = localStorage.getItem('pricepal_pending_view');
        const VALID_DEEP_LINK_VIEWS = [
          'upgrade-view', 'pricing', 'compare-parent', 'fuel-prices',
          'kitchen', 'recipes', 'settings', 'founders', 'history', 'alerts',
          'scan-parent', 'newsletter', 'referral', 'wallet',
          'help', 'contact', 'family', 'fuel-savings', 'my-blog',
          'privacy', 'terms', 'cookies'
        ];

        if (viewParam && VALID_DEEP_LINK_VIEWS.includes(viewParam)) {
          setActiveView(viewParam);
          if (['compare-parent'].includes(viewParam)) setActiveTab('compare');
          else if (['scan-parent'].includes(viewParam)) setActiveTab('scan');
          else setActiveTab('more');
          window.history.replaceState({}, '', '/app');
        } else if (pendingView && VALID_DEEP_LINK_VIEWS.includes(pendingView)) {
          localStorage.removeItem('pricepal_pending_view');
          setActiveView(pendingView);
          if (['compare-parent'].includes(pendingView)) setActiveTab('compare');
          else if (['scan-parent'].includes(pendingView)) setActiveTab('scan');
          else setActiveTab('more');
        } else if (actionParam === 'manage-billing') {
          setActiveView('settings');
          window.history.replaceState({}, '', '/app');
          // Auto-open billing portal after a short delay to let Settings render
          setTimeout(async () => {
            try {
              const portalRes = await axios.post(`${API}/stripe-payments/portal/create`, {}, { withCredentials: true });
              if (portalRes.data?.url) {
                window.open(portalRes.data.url, '_blank');
              }
            } catch (e) {
              // Portal may fail for free users — Settings page will handle it
            }
          }, 1500);
        } else if (actionParam === 'nhs-checkout') {
          window.history.replaceState({}, '', '/app');
          // Auto-create Community Stripe checkout (no trial — they already had it)
          setTimeout(async () => {
            try {
              const res = await axios.post(`${API}/stripe-payments/checkout/create`, {
                tier_id: 'community',
                billing_cycle: 'monthly',
                origin_url: window.location.origin
              }, { withCredentials: true });
              if (res.data?.checkout_url) {
                window.location.href = res.data.checkout_url;
              }
            } catch (e) {
              setActiveView('upgrade-view');
            }
          }, 500);
        }
      }
    } catch (error) {
      console.error('Authentication error:', error);
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 429) {
        setAuthError('Too many attempts. Please wait a minute and try again.');
      } else if (status === 503) {
        setAuthError(detail || 'Service temporarily busy. Please try again in a moment.');
      } else if (status === 404) {
        setAuthError('Server temporarily unavailable. Please try again in a moment.');
      } else if (status === 401) {
        setAuthError(detail || 'Invalid credentials');
      } else if (status === 400) {
        setAuthError(detail || 'Please check your details and try again.');
      } else if (status >= 500) {
        setAuthError(detail || 'Server error. Please try again in a moment.');
      } else if (!error.response) {
        // No response at all — true network error
        setAuthError('Unable to connect to server. Please check your internet connection and try again.');
      } else {
        setAuthError(detail || error.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    // End analytics session before logout
    AnalyticsTracker.endSession();

    // Call backend logout to clear cookies
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      
      
      // Use SessionManager to clear all session data
      SessionManager.clearSession();
      
      // Clear local state
      setIsAuthenticated(false);
      setUserId('');
      setEmail('');
      setPassword('');
      setUserData(null);
      setUserSubmissions([]);
      setSubscriptionInfo(null);
      
      // Clear any cached form data
      setBarcode('');
      setProductName('');
      setPrice('');
      setSearchBarcode('');
      setSearchQuery('');
      setPriceData(null);
      setSearchResults([]);
      
    } catch (error) {
      console.error('Error during logout:', error);
      // Force refresh if logout fails
      window.location.reload();
    }
  };

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setScanError('Please upload an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setScanError('Image file is too large. Maximum 5MB');
        return;
      }
      
      setProductImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      
      setScanError('');
    }
  };

  const handleSubmitScan = async (e) => {
    e.preventDefault();
    setScanSuccess('');
    setScanError('');

    // ─── iter-2026-05-19 — Client telemetry helper ───────────────────────────
    // Pings backend whenever a scan attempt fails before the POST can happen
    // (validation, session loss, network). Lets us diagnose silent failures.
    const reportClientError = (reason, extra = {}) => {
      try {
        // Fire-and-forget — never block submit flow on telemetry
        axios.post(`${API}/scan/client-error`, {
          user_id: userId || null,
          reason,
          barcode: extra.barcode ?? barcode ?? null,
          product_name: extra.product_name ?? productName ?? null,
          price: extra.price ?? price ?? null,
          retailer: extra.retailer ?? retailer ?? null,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          screen: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
          network_error: extra.network_error ?? null,
        }).catch(() => {});
      } catch (_) { /* swallow */ }
    };
    
    // DEFENSIVE: Validate user session before submission
    if (!userId || userId.trim() === '') {
      reportClientError('session_expired_no_userid');
      setScanError('User session expired. Please login again.');
      handleLogout();
      return;
    }
    
    // DEFENSIVE: Validate required fields
    if (!barcode || barcode.trim() === '') {
      reportClientError('validation_missing_barcode');
      setScanError('Barcode is required');
      return;
    }
    
    if (!productName || productName.trim() === '') {
      reportClientError('validation_missing_product_name');
      setScanError('Product name is required');
      return;
    }
    
    if (!price || parseFloat(price) <= 0) {
      reportClientError('validation_invalid_price');
      setScanError('Valid price is required');
      return;
    }
    
    if (!retailer || retailer.trim() === '') {
      reportClientError('validation_missing_retailer');
      setScanError('Retailer is required');
      return;
    }
    
    setLoading(true);

    try {
      // Calculate discount if applicable
      let discountAmount = null;
      let discountPercentage = null;
      
      if ((priceType === 'special_offer' || priceType === 'loyalty_card') && originalPrice && price) {
        const origPrice = parseFloat(originalPrice);
        const paidPrice = parseFloat(price);
        if (origPrice > paidPrice) {
          discountAmount = origPrice - paidPrice;
          discountPercentage = ((discountAmount / origPrice) * 100).toFixed(1);
        }
      }
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('user_id', userId.trim());
      formData.append('barcode', barcode.trim());
      formData.append('product_name', productName.trim());
      formData.append('price', parseFloat(price));
      formData.append('retailer', retailer.trim());
      
      // ENHANCED: Price Type & Discount Context
      formData.append('price_type', priceType);
      if (originalPrice && parseFloat(originalPrice) > 0) {
        formData.append('original_price', parseFloat(originalPrice));
      }
      if (discountAmount) formData.append('discount_amount', discountAmount);
      if (discountPercentage) formData.append('discount_percentage', discountPercentage);
      if (offerDetails && offerDetails.trim()) {
        formData.append('offer_details', offerDetails.trim());
      }
      
      if (productImage) {
        formData.append('product_image', productImage);
      }

      // Phase 4: Optional product URL for crowdsourced deep links
      if (window._productUrl && window._productUrl.trim()) {
        formData.append('product_url', window._productUrl.trim());
        formData.append('product_retailer_id', retailer.trim().toLowerCase().replace("'", "").replace(" ", "_").replace("&", ""));
      }

      const response = await axios.post(`${API}/scan`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      
      // Store submitted product for Set Alert button
      setLastSubmittedProduct({
        barcode: formData.get('barcode'),
        productName: formData.get('product_name'),
        price: parseFloat(formData.get('price')),
        retailer: formData.get('retailer')
      });
      
      // Compose status-aware message — iter-2026-05-19 P0 FIX: previously
      // always showed "✅ Scan submitted successfully!" even when the backend
      // auto-rejected the submission. Users had no idea their scan was filtered.
      const submissionStatus = response.data.submission?.status;
      const sanityIssues = response.data.submission?.sanity_issues || [];
      const community = response.data.community_thanks_message || '';
      const bonusMsg = response.data.bonus_message || '';
      let baseMsg;
      if (submissionStatus === 'rejected') {
        const reason = sanityIssues[0] || 'Submission did not pass automatic validation.';
        baseMsg = `⚠️ Scan saved but needs review: ${reason}`;
      } else if (submissionStatus === 'pending') {
        // iter162 (2026-07-23) — Savings Club paused. Focus on benefit to user.
        baseMsg = `🔍 Scan received — pending manual review. This helps sharpen your personalised price alerts.`;
      } else {
        // iter162 (2026-07-23) — Savings Club paused. Focus on personal + community benefit.
        baseMsg = `✅ Scan submitted! We'll use this to sharpen your price alerts and Weekly Shop suggestions.`;
      }
      const parts = [baseMsg];
      if (bonusMsg) parts.push(bonusMsg);
      if (community) parts.push(`💚 ${community}`);
      setScanSuccess(parts.join('\n'));
      
      // Submit barcode correction if user edited the product name
      if (autoFilledName && productName.trim() !== autoFilledName.trim() && barcode) {
        try {
          await axios.post(`${API}/barcode-overrides`, {
            barcode: barcode,
            product_name: productName.trim(),
            user_id: userId || 'anonymous'
          });
          console.log(`Barcode correction submitted: ${barcode} → ${productName}`);
        } catch (corrErr) {
          console.warn('Barcode correction failed (non-blocking):', corrErr);
        }
      }
      
      // Auto-scroll to top so user sees success message and form is ready for next scan
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // iter162 (2026-07-23) — Savings Club paused: points_balance no longer
      // incremented client-side. Backend also returns points_awarded=0.
      setUserData(prev => ({
        ...prev,
        total_submissions: prev.total_submissions + 1,
        approved_submissions: response.data.submission.status === 'approved' 
          ? prev.approved_submissions + 1 
          : prev.approved_submissions
      }));
      
      // Clear form BUT KEEP RETAILER (persists across submissions)
      setBarcode('');
      setProductName('');
      setAutoFilledName('');
      setPrice('');
      // DO NOT RESET RETAILER - it persists from localStorage
      setProductImage(null);
      setImagePreview(null);
      setPriceType('standard');
      setOriginalPrice('');
      setOfferDetails('');
      setQuickSubmitMode(false);
      setFoundProduct(null);
      
      // Refresh user data to get actual server state
      if (userId) {
        fetchUserData(userId);
        fetchUserSubmissions(userId);
      }
    } catch (error) {
      console.error('Scan submission error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Scan submission failed';
      setScanError(`❌ Error: ${errorMsg}`);

      // iter-2026-05-19: report backend POST failures so we know about
      // silent submit problems beyond client-side validation.
      reportClientError(
        error.response?.status ? `backend_${error.response.status}` : 'network_or_unknown',
        { network_error: errorMsg }
      );
      
      // DEFENSIVE: Only logout on authentication errors (401), not on 404
      if (error.response?.status === 401) {
        setScanError('Session expired. Please login again.');
        setTimeout(() => handleLogout(), 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  // BARCODE SCANNER HANDLERS
  const handleBarcodeScanned = async (scannedData) => {
    setShowScanner(false);
    
    // Handle HybridScanner object format or legacy string format
    let scannedBarcode;
    let scanError = null;
    
    if (typeof scannedData === 'string') {
      // Legacy format - simple barcode string
      scannedBarcode = scannedData;
    } else if (typeof scannedData === 'object') {
      // HybridScanner object format
      if (scannedData.error) {
        // Scanner error - show message
        scanError = scannedData.message || scannedData.error;
      } else {
        // Successful scan
        scannedBarcode = scannedData.barcode;
      }
    }
    
    // If scanner error, show it and return
    if (scanError) {
      if (activeView === 'compare') {
        setPriceError(scanError);
      } else if (activeView === 'shopping-list') {
        setScanError(scanError);
      } else {
        setScanError(scanError);
      }
      return;
    }
    
    // Handle differently based on active view
    if (activeView === 'compare') {
      // For Compare Prices: Set search barcode and trigger search
      setSearchBarcode(scannedBarcode);
      setSearchType('barcode');
      // Auto-trigger search for better UX
      try {
        setLoading(true);
        setPriceError('');
        setSearchResults([]);
        setPriceData(null);
        
        const response = await axios.get(`${API}/products/${scannedBarcode}/prices`);
        setPriceData(response.data);
        setSearchBarcode('');
      } catch (error) {
        console.error('Price comparison error:', error);
        console.error('Error response:', error.response);
        console.error('Error status:', error.response?.status);
        console.error('Error data:', error.response?.data);
        
        if (error.response?.status === 404) {
          // Product not in database - offer to add it
          setSearchBarcode(scannedBarcode); // Keep barcode visible
          setPriceError(`NEW_PRODUCT:${scannedBarcode}`); // Special error code
        } else if (error.response?.status === 500) {
          setPriceError(`Server error: ${error.response?.data?.detail || 'Please try again later'}`);
        } else if (error.message.includes('Network Error')) {
          setPriceError('Network error. Please check your connection and try again.');
        } else {
          setPriceError(`Unable to fetch prices: ${error.response?.data?.detail || error.message || 'Please try again'}`);
        }
      } finally {
        setLoading(false);
      }
    } else if (activeView === 'shopping-list') {
      // For Shopping List: Lookup product and add to list
      try {
        setScanSuccess('🔍 Looking up product...');
        const response = await axios.get(`${API}/products/${scannedBarcode}/prices`);
        const productData = response.data;
        
        if (productData && productData.canonical_name) {
          // Add to shopping list
          const newItem = {
            id: Date.now().toString(),
            barcode: scannedBarcode,
            name: productData.canonical_name,
            quantity: 1,
            checked: false
          };
          setShoppingListItems(prev => [...prev, newItem]);
          setScanSuccess(`✅ Added "${productData.canonical_name}" to your list!`);
          
          // Clear success message after 3 seconds
          setTimeout(() => setScanSuccess(''), 3000);
        } else {
          // Product not found - add with barcode as name
          const newItem = {
            id: Date.now().toString(),
            barcode: scannedBarcode,
            name: `Product ${scannedBarcode}`,
            quantity: 1,
            checked: false
          };
          setShoppingListItems(prev => [...prev, newItem]);
          setScanSuccess(`✅ Added product to your list. Edit name if needed.`);
        }
      } catch (error) {
        console.error('Shopping list scan error:', error);
        if (error.response?.status === 404) {
          // Product not in database - add anyway with barcode
          const newItem = {
            id: Date.now().toString(),
            barcode: scannedBarcode,
            name: `Product ${scannedBarcode}`,
            quantity: 1,
            checked: false
          };
          setShoppingListItems(prev => [...prev, newItem]);
          setScanSuccess(`✅ Added product to your list. Product not in database - edit name.`);
        } else {
          setScanError(`Failed to add product: ${error.message}`);
        }
      }
    } else {
      // For Submit Scan: Set barcode and lookup product
      setBarcode(scannedBarcode);
      await lookupProductByBarcode(scannedBarcode);
    }
  };
  
  // LOOKUP PRODUCT BY BARCODE (used by scanner AND manual input)
  // With OpenFoodFacts fallback for unknown products
  const lookupProductByBarcode = async (barcodeValue) => {
    // Show loading message
    setScanSuccess('🔍 Looking up product in database...');
    
    
    // Auto-lookup product details from database
    try {
      const response = await axios.get(`${API}/products/${barcodeValue}/prices`);
      
      const productData = response.data;
      
      // Check for canonical_name (the actual field name in API)
      if (productData && productData.canonical_name) {
        // Product exists - auto-fill details and enable quick submit
        const productName = productData.canonical_name;
        
        setProductName(productName);
        setAutoFilledName(productName);
        setFoundProduct(productData);
        setQuickSubmitMode(true);
        setScanSuccess(`Found "${productName}" — just enter the price!`);
        setScanError('');
        
      } else {
        // Product data exists but no canonical_name
        setQuickSubmitMode(false);
        setFoundProduct(null);
        setScanSuccess('');
        setScanError(`Product data incomplete. Please enter the product name below.`);
      }
    } catch (error) {
      // Product lookup failed — handle gracefully
      if (error.response?.status === 404) {
        // Product not in local database - try OpenFoodFacts as fallback
        setQuickSubmitMode(false);
        setFoundProduct(null);
        setScanSuccess('Checking external product database...');
        
        try {
          const offResponse = await axios.get(`${API}/openfoodfacts/product/${barcodeValue}`);
          
          if (offResponse.data?.success && offResponse.data?.product) {
            const offProduct = offResponse.data.product;
            // Handle both canonical_name and name fields
            const offProductName = offProduct.canonical_name || offProduct.name || '';
            const offBrand = offProduct.brand || '';
            
            if (offProductName) {
              // Check if brand is already in the name to avoid duplication
              const fullName = (offBrand && !offProductName.toLowerCase().includes(offBrand.toLowerCase())) 
                ? `${offBrand} ${offProductName}` 
                : offProductName;
              
              setProductName(fullName);
              setAutoFilledName(fullName);
              setQuickSubmitMode(false); // External product — user should verify details
              setFoundProduct(null);
              setScanSuccess(`Found "${fullName}" from external database — please verify and enter the price.`);
              setScanError('');
            } else {
              // No product name found
              setQuickSubmitMode(false);
              setFoundProduct(null);
              setScanSuccess('');
              setScanError('');
              setScanSuccess(`New product! This barcode isn't in any database yet. Please enter the product name below.`);
            }
          } else {
            // Not found in OpenFoodFacts either - truly new product
            setQuickSubmitMode(false);
            setFoundProduct(null);
            setScanSuccess('');
            setScanError('');
            setScanSuccess(`New product! This barcode isn't in any database yet. Please enter the product name below.`);
          }
        } catch (offError) {
          // OpenFoodFacts lookup failed - show new product message
          setQuickSubmitMode(false);
          setFoundProduct(null);
          setScanSuccess('');
          setScanError('');
          setScanSuccess(`New product! This barcode isn't in our database yet. Please enter the product name below.`);
        }
      } else {
        // Other error (network, server, etc)
        setScanSuccess('');
        setScanError(`⚠️ Unable to check database. You can still submit by entering the product name below.`);
      }
    }
    
    // Clear messages after 10 seconds
    setTimeout(() => {
      setScanSuccess('');
    }, 10000);
  };
  
  // Handle manual barcode input (when user types and moves away)
  const handleBarcodeBlur = async (e) => {
    const barcodeValue = e.target.value.trim();
    
    // Only lookup if barcode is valid format (8-14 digits)
    if (barcodeValue && /^\d{8,14}$/.test(barcodeValue)) {
      // Validate barcode checksum before lookup
      const validation = validateBarcode(barcodeValue);
      
      if (!validation.valid) {
        setScanError(`❌ Invalid barcode checksum. Please check the number and try again.`);
        setScanSuccess('');
        return;
      }
      
      await lookupProductByBarcode(barcodeValue);
    }
  };

  const handleCloseScan = () => {
    setShowScanner(false);
  };

  const handlePriceSearch = async (e) => {
    e.preventDefault();
    setPriceError('');
    setPriceData(null);
    setSearchResults([]);
    setLoading(true);

    try {
      if (searchType === 'barcode') {
        // Direct barcode search
        try {
          const response = await axios.get(`${API}/products/${searchBarcode}/prices`);
          setPriceData(response.data);
        } catch (barcodeError) {
          if (barcodeError.response?.status === 404) {
            // Product not in database - use special error code
            setPriceError(`NEW_PRODUCT:${searchBarcode}`);
            setLoading(false);
            return;
          } else {
            throw barcodeError; // Re-throw other errors
          }
        }
      } else {
        // Name search with similarity matching
        const response = await axios.get(`${API}/products/search`, {
          params: {
            query: searchQuery,
            limit: 10,
            min_confidence: 0.65
          }
        });
        
        
        if (response.data.count === 0) {
          setPriceError('No products found matching your search. This product may need to be added to the database first via Submit Scan.');
        } else {
          // 🔥 FIX: Always auto-select the best match (highest confidence)
          // This makes ALL searches consistent with camera scan behavior
          const bestMatch = response.data.results[0]; // Already sorted by confidence
          const priceResponse = await axios.get(`${API}/products/${bestMatch.barcode}/prices`);
          setPriceData({
            ...priceResponse.data,
            match_confidence: bestMatch.confidence,
            match_percentage: bestMatch.match_percentage
          });
          
          // Store other matches in case user wants to see alternatives
          if (response.data.count > 1) {
            setSearchResults(response.data.results.slice(1)); // Store remaining matches
          }
        }
      }
    } catch (error) {
      // Handle error detail properly (could be string or object)
      let errorMsg = 'Product not found. Try searching by name for similar products.';
      if (error.response?.data) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        }
      }
      setPriceError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // SMART AUTOMATIC SEARCH - Handles both barcode and name queries
  const handleUnifiedSearch = async (query) => {
    if (!query || query.trim() === '') {
      setSearchResults([]);
      setPriceData(null);
      setPriceError('');
      return;
    }

    const trimmedQuery = query.trim();
    setLoading(true);
    setPriceError('');
    setSearchResults([]);
    setPriceData(null);

    try {
      // Detect if query is a barcode (8-14 digits) or product name
      const isBarcode = /^\d{8,14}$/.test(trimmedQuery);

      if (isBarcode) {
        // Direct barcode lookup
        const response = await axios.get(`${API}/products/${trimmedQuery}/prices`);
        setPriceData(response.data);
        setSearchResults([]);
      } else {
        // Product name search (ML-based)
        const response = await axios.get(`${API}/products/search`, {
          params: { query: trimmedQuery, limit: 10 }
        });

        if (response.data && response.data.results && response.data.results.length > 0) {
          // 🔥 FIX: Auto-select best match and show price comparison directly
          const bestMatch = response.data.results[0];
          const priceResponse = await axios.get(`${API}/products/${bestMatch.barcode}/prices`);
          setPriceData({
            ...priceResponse.data,
            match_confidence: bestMatch.confidence,
            match_percentage: bestMatch.match_percentage
          });
          setSearchResults([]); // Clear intermediate results
        } else {
          setPriceError('❌ No products found matching your search. This product may need to be added to the database first via Submit Scan.');
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      if (error.response?.status === 404) {
        setPriceError('❌ No products found matching your search. This product may need to be added to the database first via Submit Scan.');
      } else if (error.message.includes('Network Error')) {
        setPriceError('Network error. Please check your connection and try again.');
      } else {
        setPriceError(`Unable to fetch prices: ${error.response?.data?.detail || error.message || 'Please try again'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Debounced search for text input (500ms delay)
  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      return;
    }

    const timeoutId = setTimeout(() => {
      handleUnifiedSearch(searchQuery);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Refresh receipt history when navigating to history view
  useEffect(() => {
    if (activeView === 'history' && userId) {
      axios.get(`${API}/price-hub/my-receipts`, {
        params: { user_id: userId, limit: 50 },
        withCredentials: true
      }).then(res => {
        if (res?.data) {
          setReceiptHistory(res.data);
        }
      }).catch(err => console.error('Receipt history refresh failed:', err.message));
    }
  }, [activeView, userId]);

  // Handle product selection from search results
  const handleSelectProduct = async (barcode, productData) => {
    setLoading(true);
    setPriceError('');

    try {
      const response = await axios.get(`${API}/products/${barcode}/prices`);
      setPriceData(response.data);
      setSearchResults([]);
    } catch (error) {
      console.error('Error fetching product prices:', error);
      // Handle error detail properly (could be string or object)
      let errorMsg = 'Failed to load price data';
      if (error.response?.data) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (detail && typeof detail === 'object') {
          errorMsg = JSON.stringify(detail);
        } else if (error.response.data.message) {
          errorMsg = error.response.data.message;
        }
      }
      setPriceError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Login/Signup View
  if (!isAuthenticated) {
    return (
      <AuthScreen
        isSignup={isSignup} setIsSignup={setIsSignup}
        email={email} setEmail={setEmail}
        password={password} setPassword={setPassword}
        name={name} setName={setName}
        showUserPassword={showUserPassword} setShowUserPassword={setShowUserPassword}
        marketingConsent={marketingConsent} setMarketingConsent={setMarketingConsent}
        referralCode={referralCode} setReferralCode={setReferralCode}
        rememberMe={rememberMe} setRememberMe={setRememberMe}
        isBetaSignup={isBetaSignup}
        forceSignupMode={forceSignupMode}
        authError={authError} setAuthError={setAuthError}
        loading={loading}
        handleAuth={handleAuth}
      />
    );
  }
  // Check if user is Alpha/Beta Tester - used across multiple views
  const isAlphaTester = 
    userData?.is_beta_tester || 
    userData?.is_alpha_tester ||
    userData?.subscription_tier === 'ALPHA_TESTER' ||
    userData?.subscription_tier === 'VIP_FOUNDER' || 
    userData?.subscription_tier === 'EARLY_FOUNDER' ||
    userData?.lifetime_access ||
    (userData?.admin_override?.active && userData?.admin_override?.forced_tier === 'founder');

  // User Dashboard View
  if (activeView === 'dashboard') {
    return (
      <DashboardView
        userId={userId} userData={userData} subscriptionInfo={subscriptionInfo} isAlphaTester={isAlphaTester}
        handleLogout={handleLogout} handleNavigation={handleNavigation}
        setActiveTab={setActiveTab} setActiveView={setActiveView} activeTab={activeTab} handleTabChange={handleTabChange}
        showBetaModal={showBetaModal} setShowBetaModal={setShowBetaModal}
        showFounderModal={showFounderModal} setShowFounderModal={setShowFounderModal} founderModalTier={founderModalTier}
        showProModal={showProModal} setShowProModal={setShowProModal} isProFamily={isProFamily}
        showFreeModal={showFreeModal} setShowFreeModal={setShowFreeModal}
      />
    );
  }

  // All other views handled by UserViewRouter
  const viewRouterResult = UserViewRouter({
    activeView, activeTab, handleTabChange,
    userId, email, userData, userTier,
    handleBack, handleLogout, handleNavigation,
    setActiveView, setActiveTab,
    setShowUpgradeModal, setUpgradeReason,
    isAlphaTester,
    barcode, setBarcode, productName, setProductName,
    price, setPrice, retailer, setRetailer,
    priceType, setPriceType, originalPrice, setOriginalPrice,
    offerDetails, setOfferDetails,
    scanSuccess, setScanSuccess, scanError,
    loading, showScanner, setShowScanner,
    imagePreview, productImage, setProductImage, setImagePreview,
    quickSubmitMode, setQuickSubmitMode, foundProduct, setFoundProduct,
    lastSubmittedProduct, setLastSubmittedProduct,
    handleBarcodeScanned, handleCloseScan, handleSubmitScan,
    lookupProductByBarcode, handleImageChange, handleBarcodeBlur,
    userSubmissions, receiptHistory, setReceiptHistory,
    shoppingListItems, setShoppingListItems,
    moreReturnSection, setMoreReturnSection,
    showBetaModal, setShowBetaModal,
    showFounderModal, setShowFounderModal, founderModalTier,
    showProModal, setShowProModal, isProFamily,
    showFreeModal, setShowFreeModal,
    legalPageModal, setLegalPageModal,
    showWelcomeTour, handleCloseTour: handleCloseTour,
  });
  if (viewRouterResult) return (
    <Suspense fallback={
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-3 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    }>
      {viewRouterResult}
    </Suspense>
  );

  return (
    <>
      {/* LEGAL PAGE MODAL */}
      {legalPageModal && (
        <LegalPageModal 
          page={legalPageModal}
          onClose={() => setLegalPageModal(null)}
        />
      )}
      
      {/* BETA WELCOME MODAL */}
      {showBetaModal && (
        <BetaWelcomeModal 
          onClose={() => setShowBetaModal(false)} 
          userName={userData?.name || 'Explorer'} 
        />
      )}
      
      {/* FOUNDER WELCOME MODAL */}
      {showFounderModal && (
        <FounderWelcomeModal
          onClose={() => setShowFounderModal(false)}
          userName={userData?.name || 'Founder'}
          tier={founderModalTier}
        />
      )}
      
      {/* PRO WELCOME MODAL */}
      {showProModal && (
        <ProWelcomeModal
          onClose={() => setShowProModal(false)}
          userName={userData?.name || 'Pro Member'}
          isFamily={isProFamily}
        />
      )}
      
      {/* FREE USER WELCOME MODAL */}
      {showFreeModal && (
        <FreeWelcomeModal
          onClose={() => setShowFreeModal(false)}
          userName={userData?.name || 'there'}
          onUpgrade={() => handleNavigation('upgrade')}
        />
      )}
      
      {/* UPGRADE MODAL */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        triggerReason={upgradeReason}
        userData={userData}
      />

      {/* Welcome Tour */}
      {showWelcomeTour && (
        <WelcomeTour
          userData={userData}
          onClose={handleCloseTour}
        />
      )}
    </>
  );
}

export default UserApp;
